import { TFunction } from 'i18next'
import { UseFormReturn } from 'react-hook-form'
import { FieldType, IFormConfig } from 'types'
import { CountryMultiSelect } from 'components/CountryMultiSelect'
import { PhoneNumberField } from 'components/PhoneNumberField'
import { AddressFields } from 'components/AddressFields'
import { FullNameFields } from 'components/FullNameFields'
import { yesNoOptions } from 'consts'
import {
  BeneficialOwner,
  beneficialOwnerSchema
} from 'pages/Onboarding/OnboardingRegion/UsRegion/UsRegionStepsConfigs'
import { CountryAlpha2 } from 'generated/graphql'

const identificationTypeOptions = [
  { value: 'itin', label: 'ITIN' },
  { value: 'passport', label: 'Passport' }
]

type DisabledFields = {
  disableSigner?: boolean
  disableContact?: boolean
}

export const getBeneficialOwnerModalConfig = (
  t: TFunction,
  { disableSigner, disableContact }: DisabledFields
): IFormConfig<BeneficialOwner> => ({
  schema: beneficialOwnerSchema,
  fields: [
    {
      name: 'relation.title',
      label: t('beneficialOwner.fields.title.label'),
      placeholder: t('beneficialOwner.fields.title.placeholder'),
      tooltip: t('beneficialOwner.fields.title.tooltip')
    },
    {
      name: 'individual.firstName',
      type: FieldType.CUSTOM,
      label: t('beneficialOwner.fields.firstName.label'),
      tooltip: t('beneficialOwner.fields.firstName.tooltip'),
      CustomComponent: FullNameFields,
      customComponentProps: {
        fields: [
          {
            name: 'individual.firstName',
            label: t('beneficialOwner.fields.firstName.label'),
            placeholder: t('beneficialOwner.fields.firstName.placeholder')
          },
          {
            name: 'individual.middleName',
            label: t('beneficialOwner.fields.middleName.label'),
            placeholder: t('beneficialOwner.fields.middleName.placeholder')
          },
          {
            name: 'individual.lastName',
            label: t('beneficialOwner.fields.lastName.label'),
            placeholder: t('beneficialOwner.fields.lastName.placeholder')
          }
        ]
      }
    },
    {
      name: 'individual.dateOfBirth',
      type: FieldType.DATE,
      label: t('beneficialOwner.fields.dateOfBirth.label'),
      placeholder: 'DD/MM/YY',
      tooltip: t('beneficialOwner.fields.dateOfBirth.tooltip')
    },
    {
      name: 'relation.percentageOfShares',
      label: t('beneficialOwner.fields.percentageOfShares.label'),
      placeholder: t('beneficialOwner.fields.percentageOfShares.placeholder'),
      htmlType: 'number',
      tooltip: t('beneficialOwner.fields.percentageOfShares.tooltip')
    },
    {
      name: 'individual.nationalities',
      type: FieldType.CUSTOM,
      label: t('beneficialOwner.fields.nationalities.label'),
      placeholder: t('beneficialOwner.fields.nationalities.placeholder'),
      tooltip: t('beneficialOwner.fields.nationalities.tooltip'),
      CustomComponent: CountryMultiSelect,
      customComponentProps: {
        onChange: (
          values: string[],
          methods: UseFormReturn<BeneficialOwner>
        ) => {
          if (values.includes(CountryAlpha2.Us)) {
            methods.setValue('individual.identificationDocumentType', '')
            methods.setValue('individual.idNumber', '')
          } else {
            methods.setValue('individual.ssn', '')
          }
        }
      }
    },
    {
      name: 'individual.ssn',
      format: '###-##-####',
      label: t('beneficialOwner.fields.ssn.label'),
      placeholder: t('beneficialOwner.fields.ssn.placeholder'),
      tooltip: t('beneficialOwner.fields.ssn.tooltip'),
      ignoreField: methods =>
        !methods.watch('individual.nationalities')?.includes(CountryAlpha2.Us)
    },
    {
      name: 'individual.identificationDocumentType',
      type: FieldType.SELECT,
      label: t('beneficialOwner.fields.identificationDocumentType.label'),
      placeholder: t(
        'beneficialOwner.fields.identificationDocumentType.placeholder'
      ),
      tooltip: t('beneficialOwner.fields.identificationDocumentType.tooltip'),
      options: identificationTypeOptions,
      ignoreField: methods =>
        methods.watch('individual.nationalities')?.includes(CountryAlpha2.Us)
    },
    {
      name: 'individual.idNumber',
      label: t('beneficialOwner.fields.idNumber.label'),
      placeholder: t('beneficialOwner.fields.idNumber.placeholder'),
      tooltip: t('beneficialOwner.fields.idNumber.tooltip'),
      ignoreField: methods =>
        methods.watch('individual.nationalities')?.includes(CountryAlpha2.Us)
    },
    {
      name: 'individual.residentialAddress.addressLine',
      type: FieldType.CUSTOM,
      label: t('beneficialOwner.fields.addressLine.label'),
      tooltip: t('beneficialOwner.fields.addressLine.tooltip'),
      CustomComponent: AddressFields,
      customComponentProps: {
        withCountry: true,
        country: CountryAlpha2.Us,
        placeholders: {
          country: t('beneficialOwner.fields.country.placeholder'),
          addressLine: t('beneficialOwner.fields.addressLine.placeholder'),
          addressLine2: t('beneficialOwner.fields.addressLine2.placeholder'),
          city: t('beneficialOwner.fields.city.placeholder'),
          state: t('beneficialOwner.fields.state.placeholder'),
          zip: t('beneficialOwner.fields.zip.placeholder')
        }
      }
    },
    {
      name: 'individual.phoneNumber',
      type: FieldType.CUSTOM,
      label: t('beneficialOwner.fields.phoneNumber.label'),
      tooltip: t('beneficialOwner.fields.phoneNumber.tooltip'),
      CustomComponent: PhoneNumberField
    },
    {
      name: 'individual.email',
      label: t('beneficialOwner.fields.email.label'),
      placeholder: t('beneficialOwner.fields.email.placeholder'),
      tooltip: t('beneficialOwner.fields.email.tooltip')
    },
    {
      name: 'relation.signsTaxDocuments',
      type: FieldType.RADIO,
      label: t('beneficialOwner.fields.signsTaxDocuments.label'),
      tooltip: t('beneficialOwner.fields.signsTaxDocuments.tooltip'),
      options: yesNoOptions,
      disabled: disableSigner
    },
    {
      name: 'relation.isContact',
      type: FieldType.RADIO,
      label: t('beneficialOwner.fields.isContact.label'),
      tooltip: t('beneficialOwner.fields.isContact.tooltip'),
      options: yesNoOptions,
      disabled: disableContact
    }
  ]
})
